/* eslint-disable react/no-find-dom-node */
import chain from 'chain-function';
import classnamesBind from 'classnames/bind';
import { findDOMNode } from 'react-dom';
import FocusManager from '@concur/nui-widgets/lib/utils/focusManager';
import onClickOutside from 'react-onclickoutside';
import Popper from '@concur/nui-widgets/lib/utils/_Popper';
import { POPPER_PLACEMENTS } from '@concur/nui-widgets/lib/utils/StyleConfig';
import PropTypes from 'prop-types';
import React from 'react';
import shortId from '@concur/nui-widgets/lib/utils/shortId';
import { withThemeStyles } from '@concur/ui-theming';
import tabbable, { isTabbable } from 'tabbable';
import isVisible from '../utils/isVisible';
import * as styles from './Overlay-*.css';

const CSS_BLOCK = 'sapcnqr-overlay';

export class OverlayComponent extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            show: props.show,
        };
        this.childRef = React.createRef();
        this.overlay = null;
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            show: nextProps.show,
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.show !== this.state.show) {
            this.handleFocusManager();

            if (prevState.show && !this.state.show) {
                this.props.onHide();
            }
        }
    }

    handleClickOutside = (e) => {
        if (e && this.overlay?.contains(e.target)) {
            return;
        }

        this.setState({
            show: false,
        });
    }

    handleFocusManager = () => {
        let targetTabbableNode;
        if (this.childRef.current) {
            if (isTabbable(this.childRef.current)) {
                targetTabbableNode = this.childRef.current;
            } else {
                [targetTabbableNode] = tabbable(this.childRef.current);
            }
        }
        if (this.state.show && this.overlay) {
            const getTabbableNodes = this.props.includeTargetInTabFlow
                ? () => tabbable(this.overlay).concat([targetTabbableNode])
                : () => tabbable(this.overlay);
            this.focusManager = new FocusManager(
                this.overlay,
                getTabbableNodes,
            );
        } else if (targetTabbableNode && isVisible(targetTabbableNode)) {
            targetTabbableNode.focus();
        }
    }

    handleClick = () => {
        this.setState({
            show: true,
        });
    }

    render() {
        // the following props marked with "eslint-disable-line" are due to these
        // getting injected into the props object by react-onclickoutside
        const {
            children,
            className,
            classNameMap,
            content,
            contentClassName,
            disableEdgeDetection,
            disableOnClickOutside,  // eslint-disable-line
            enableOnClickOutside,  // eslint-disable-line
            eventTypes,  // eslint-disable-line
            id,
            placement,
            show,
            onHide,
            outsideClickIgnoreClass,  // eslint-disable-line
            popperProps,
            preventDefault,  // eslint-disable-line
            stopPropagation,  // eslint-disable-line
            targetClassName,
            includeTargetInTabFlow,
            ...otherProps
        } = this.props;

        const classnames = classnamesBind.bind(classNameMap);

        const popoverClassName = classnames(
            CSS_BLOCK,
            className,
        );

        const contentClasses = classnames(
            `${CSS_BLOCK}__content`,
            contentClassName,
        );

        const extendedPopperProps = {
            'aria-describedby': `${id}-content`,
            innerRef: (c) => {
                this.overlay = findDOMNode(c);
            },
            role: 'dialog',
            ...popperProps,
        };

        return (
            <Popper
                disableEdgeDetection={disableEdgeDetection}
                disableHideWhenTargetIsHidden
                onEscapeKey={this.handleClickOutside}
                popperClassName={popoverClassName}
                popperPlacement={placement}
                popperProps={extendedPopperProps}
                show={this.state.show}
                targetClassName={targetClassName}
                targetComponent={React.cloneElement(React.Children.only(children), {
                    onClick: chain(this.handleClick, children.props.onClick),
                    ref: this.childRef,
                })}
            >
                <div
                    {...otherProps}
                    className={contentClasses}
                    id={`${id}-content`}
                >
                    {content}
                </div>
            </Popper>
        );
    }
}

OverlayComponent.displayName = 'Overlay';

OverlayComponent.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    content: PropTypes.node,
    contentClassName: PropTypes.string,
    disableEdgeDetection: PropTypes.bool,
    id: PropTypes.string,
    placement: PropTypes.oneOf(POPPER_PLACEMENTS),
    popperProps: PropTypes.object,
    show: PropTypes.bool,
    targetClassName: PropTypes.string,
    includeTargetInTabFlow: PropTypes.bool,
    onHide: PropTypes.func,
};

OverlayComponent.defaultProps = {
    id: shortId.generate(),
    placement: 'bottom',
    onHide: () => {},
};

export default withThemeStyles(onClickOutside(OverlayComponent), styles);
